"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { CheckIcon, ShareIcon } from "@/components/appIcons";
import { buildCurrentShareUrl } from "@/components/build-current-share-url";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";

interface ShareButtonProps {
  title: string;
  className?: string;
}

export function ShareButton({ title, className }: ShareButtonProps) {
  const t = useTranslations("globals.profile");
  const [copied, setCopied] = useState(false);
  const Icon = copied ? CheckIcon : ShareIcon;
  const { data: session } = useSession();
  const handleShare = async () => {
    const fullUrl = buildCurrentShareUrl(session?.user);
    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({ title, url: fullUrl });
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(t("linkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available -- ignore
    }
  };

  return (
    <button
      onClick={handleShare}
      aria-label={t("share")}
      className={cn(
        "relative after:absolute after:-inset-1.5 after:content-[''] flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors",
        className,
      )}
    >
      <Icon className="text-base" />
      <span className="share-button-label">{t("share")}</span>
    </button>
  );
}
